import React, { useEffect, useState } from 'react';
import { useApps } from '@/contexts/AppsContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Activity,
  Users,
  Boxes,
  Heart,
  Shield,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from 'lucide-react';

export default function AdminOverviewPage() {
  const { apps } = useApps();
  const { user } = useAuth();

  const uniqueOwners = [...new Set(apps.map((a) => a.adminEmail).filter(Boolean))];
  const activeApps = apps.filter((a) => a.status === 'active');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Platform Overview</h1>
        <p className="text-muted-foreground">
          System administrator view. Logged in as {user?.email}.
        </p>
      </div>

      {/* Platform Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Apps</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{apps.length}</div>
            <p className="text-xs text-muted-foreground">
              {activeApps.length} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Owners</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{uniqueOwners.length}</div>
            <p className="text-xs text-muted-foreground">
              Distinct admin emails
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Heartbeat</CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <span className="text-lg font-semibold text-emerald-500">Healthy</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Running every 5 min
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <span className="text-lg font-semibold text-emerald-500">Operational</span>
            </div>
            <p className="text-xs text-muted-foreground">
              All services up
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Infrastructure Status */}
      <Card>
        <CardHeader>
          <CardTitle>Infrastructure</CardTitle>
          <CardDescription>
            Service health across the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {[
              { name: 'Cognito Auth', status: 'healthy', detail: 'trucontext-developers' },
              { name: 'Control Plane API', status: 'healthy', detail: 'platform.trucontext.ai' },
              { name: 'Data Plane API', status: 'healthy', detail: 'api.trucontext.ai' },
              { name: 'Neo4j Graph', status: 'healthy', detail: 'Cognitive graph store' },
              { name: 'DynamoDB', status: 'healthy', detail: 'App & event tables' },
              { name: 'WebSocket', status: 'healthy', detail: 'Real-time push (Mind page)' },
              { name: 'Heartbeat Worker', status: 'idle', detail: 'No active apps with events' },
              { name: 'CloudFront CDN', status: 'healthy', detail: 'app.trucontext.ai' },
              { name: 'Billing API', status: 'not_deployed', detail: 'Not yet implemented' },
            ].map((svc) => (
              <div key={svc.name} className="flex items-center gap-3 rounded-lg border p-3">
                {svc.status === 'healthy' && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />}
                {svc.status === 'idle' && <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />}
                {svc.status === 'not_deployed' && <XCircle className="h-4 w-4 text-muted-foreground shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{svc.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{svc.detail}</p>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    'ml-auto shrink-0 text-[10px]',
                    svc.status === 'healthy' && 'border-emerald-500/50 text-emerald-500',
                    svc.status === 'idle' && 'border-amber-500/50 text-amber-500',
                    svc.status === 'not_deployed' && 'border-muted-foreground/50 text-muted-foreground',
                  )}
                >
                  {svc.status === 'healthy' ? 'OK' : svc.status === 'idle' ? 'Idle' : 'N/A'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Apps Quick List */}
      <Card>
        <CardHeader>
          <CardTitle>Apps</CardTitle>
          <CardDescription>
            All registered apps on the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {apps.map((app) => (
              <div
                key={app.appId}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{app.name}</p>
                  <p className="text-xs text-muted-foreground">{app.adminEmail || 'No owner'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px]',
                      app.status === 'active'
                        ? 'border-emerald-500/50 text-emerald-500'
                        : 'border-muted-foreground/50 text-muted-foreground',
                    )}
                  >
                    {app.status}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {app.planTier || 'free'}
                  </Badge>
                </div>
              </div>
            ))}
            {apps.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No apps registered yet.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function cn(...args) {
  return args.filter(Boolean).join(' ');
}
